import { Box, Typography } from "@material-ui/core"
import { getAiImageAiSetting } from "api"
import { errorAndFocusStyles } from "components/GenericInput/GenericInput"
import FormInput from "components/QuickPurchasePage/Form/FormInput"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import NextLink from "components/Reusable/Bit/NextLink"
import Image from "next/image"
import { useEffect, useState } from "react"
import { isDesktop } from "react-device-detect"
import styled from "styled-components"
import colors from "theme/colors"
import * as Yup from "yup"
import { useCreateAiContext } from "./CreateAiContext"

const StyledTitleContainer = styled.div`
  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    align-items: center;
  }
`

const StyledTitle = styled(Typography).attrs({
  component: "h2",
  variant: "h2",
})`
  color: ${colors.black};
  white-space: nowrap;
  font-size: 26px;
  line-height: 0.6;
  font-family: Speedee, NarkisBlock, sans-serif;
  margin-bottom: 16px;
  text-align: right;
  position: relative;

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-left: 16px;
    &:after {
      content: "";
      width: 1px;
      margin-right: 16px;
      height: 32px;
      position: absolute;
      background-color: ${colors.black};
      top: -10px;
    }
  }
`
const StyledExplanation = styled(Typography).attrs({
  variant: "body1",
})`
  font-size: 24px;
  font-weight: 100;
  color: #000;
  line-height: 0.92;
  text-align: right;
  margin-bottom: 16px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    font-size: 18px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-right: 16px;
  }
`
const StyledBlessingAiValue = styled.div`
  width: 200px;
  color: ${colors.black};
  border: solid 1px ${colors.disabled};
  border-radius: 4px;
  margin-bottom: 32px;
  font-size: 14px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  gap: 8px;
  font-family: Speedee, NarkisBlock, sans-serif;
  font-size: 18px;
`
const StyledTextBox = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: -32px;
  margin-bottom: 60px;

  ${(p) => p.theme.breakpoints.up("md")} {
    width: 680px;
  }
`
const StyledTypography = styled(Typography).attrs({ variant: "body1" })`
  font-size: 18px;
`
const StyledText = styled(Typography).attrs({ variant: "body1" })`
  font-family: "NarkisBlock", sans-serif;
  font-weight: 400;
  font-size: 18px;
  z-index: 1;
  a {
    margin-left: 5px;
    text-decoration: underline;
  }
`

const StyledFormInput = styled(FormInput)`
  .MuiInputBase-root {
    border: none;
    min-height: 80px;

    ${(p) => p.theme.breakpoints.down("sm")} {
      textarea {
        font-size: 20px;
      }
    }
    ${(p) => p.theme.breakpoints.up("md")} {
      width: 680px;
      min-height: 120px;
      textarea {
        font-size: 18px;
      }
    }
  }

  ${errorAndFocusStyles}

  .MuiInputLabel-outlined {
    transform: translate(-21px, 9px) scale(1);
    font-size: 18px;
    color: var(--Grey, #757575);
  }

  .MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: translate(-13px, 5px) scale(1);
    font-size: 18px;
  }

  textarea {
    position: relative;
    top: 6px;
  }

  ${(p) => p.theme.breakpoints.down("sm")} {
    .MuiOutlinedInput-input {
      position: relative;
      top: 26px;
    }
  }
`

const StyledFormContainer = styled.div`
  display: flex;
  justify-content: flex-start;

  ${(p) => p.theme.breakpoints.down("sm")} {
    .MuiFormControl-root {
      width: 100%;
    }
  }
`

const validationSchema = Yup.object().shape({
  senderBlessingAi: Yup.string(),
})

const initialValues = {
  senderBlessingAi: "",
}
const SenderAi = () => {
  const { senderBlessingAi, setSenderBlessingAi, setLimitAiImages } =
    useCreateAiContext()
  const [initialSenderBlessingAi, setInitialSenderBlessingAi] =
    useState<string>("")

  let tmpBlessing = ""

  useEffect(() => {
    const getSettings = async () => {
      const res = await getAiImageAiSetting()
      if (res.data.errorCode === 0) {
        setInitialSenderBlessingAi(res.data.body.defaultText)
        setLimitAiImages(res.data.body.maxGenerate)
      }
    }

    getSettings()
  }, [])

  return (
    <>
      <StyledTitleContainer>
        <StyledTitle>רוצה לכתוב משהו?</StyledTitle>
        <StyledExplanation>
          במידה ולא כתבת מסר, נשלח בשמך מסר מוכן מאיתנו{" "}
        </StyledExplanation>
      </StyledTitleContainer>

      <StyledBlessingAiValue>
        <Image src="/img/icons/ai-logo.svg" width={24} height={24} alt="" />
        {senderBlessingAi.length > 0
          ? senderBlessingAi
          : initialSenderBlessingAi}
      </StyledBlessingAiValue>
      <FormikForm
        validate={(values) => {
          setSenderBlessingAi(values.senderBlessingAi)
        }}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={async (values) => {
          console.log(values)
        }}
        alwaysValidate
        render={(formik) => {
          tmpBlessing = formik?.values.senderBlessingAi

          return (
            <StyledFormContainer>
              <StyledFormInput
                name="senderBlessingAi"
                label="כתיבת מילים לצד התמונה"
                type="text"
                InputProps={{ multiline: true }}
                minRows={4}
                maxLength={20}
                topMargin={isDesktop && "0"}
                id="senderBlessingAi"
                value={senderBlessingAi || null}
              />
            </StyledFormContainer>
          )
        }}
      />
      <StyledTextBox>
        <StyledTypography>
          <Box component="span">{senderBlessingAi?.length || 0}</Box>
          /20
        </StyledTypography>
        <StyledText>
          <NextLink
            href="/ethics"
            onClick={() => {
              setSenderBlessingAi(tmpBlessing)
            }}
          >
            הקוד האתי שלנו
          </NextLink>
          לכתיבת נאותה
        </StyledText>
      </StyledTextBox>
    </>
  )
}

export default SenderAi
